import React, { useEffect, useRef } from "react";
import { Field, useFormikContext } from "formik";
import {
  TURNSTILE_ACTION,
  TURNSTILE_RESET_EVENT,
} from "utils/turnstile";

const hiddenFieldStyle = { display: "none" };
const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const TurnstileWidget = () => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;
    const renderWidget = () => {
      if (
        cancelled ||
        !containerRef.current ||
        !window.turnstile ||
        widgetIdRef.current !== null
      ) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: TURNSTILE_ACTION,
        theme: "auto",
        appearance: "interaction-only",
        callback: (token) => setFieldValue("turnstile_token", token),
        "expired-callback": () => setFieldValue("turnstile_token", ""),
        "error-callback": () => setFieldValue("turnstile_token", ""),
        "timeout-callback": () => setFieldValue("turnstile_token", ""),
      });
    };

    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      // Turnstile serves a versionless script, so a stable SRI hash is not available.
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener("load", renderWidget);
    renderWidget();

    return () => {
      cancelled = true;
      script.removeEventListener("load", renderWidget);
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [setFieldValue]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    const resetWidget = () => {
      setFieldValue("turnstile_token", "");
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    };

    window.addEventListener(TURNSTILE_RESET_EVENT, resetWidget);
    return () => window.removeEventListener(TURNSTILE_RESET_EVENT, resetWidget);
  }, [setFieldValue]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || values.turnstile_token !== "") return;
    if (!window.turnstile || widgetIdRef.current === null) return;

    window.turnstile.reset(widgetIdRef.current);
  }, [values.turnstile_token]);

  if (!TURNSTILE_SITE_KEY) return null;

  return <div ref={containerRef} />;
};

const FormBotProtection = () => (
  <>
    <div style={hiddenFieldStyle} aria-hidden="true">
      <Field
        name="company_website"
        type="text"
        autoComplete="off"
        tabIndex={-1}
      />
      <Field name="form_loaded_at" type="hidden" />
      <Field name="turnstile_token" type="hidden" />
    </div>
    <TurnstileWidget />
  </>
);

export default FormBotProtection;
